import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { config } from '../../../../config/env';
import {
  AnalyticsEvent,
  FeatureEnum,
  GroupIdEnum,
  OwnerEnum,
} from 'app/modules/Segment/types';
import { eventTracker } from 'app/modules/Segment';
import { useDrivePlanExplorationBridge } from 'app/hooks/useDrivePlanExplorationBridge';
import type { DrivePlanExplorationReason } from 'app/components/drive/DriveFrame/types';
import {
  ALLOWED_DRIVE_IFRAME_SEGMENT_EVENTS,
  HUBSPOT_FILTER_DRIVE_IFRAME_EVENTS,
} from './analyticsEvents';
import {
  OPEN_PREVIEW_PARAM,
  readOpenPreviewParam,
  stripOpenPreviewParam,
} from './openPreviewParam';

/**
 * Embute o jusfy-drive-front via iframe e implementa o lado host do bridge
 * de autenticação (mesmo padrão do CustomerFrame): ao receber
 * DRIVE_IFRAME_READY do microfrontend, responde com o JWT do monólito.
 */

const getToken = (): string => {
  try {
    const persisted = localStorage.getItem('persist:v713-demo1-auth');
    if (persisted) {
      const parsed = JSON.parse(persisted);
      return (parsed.authToken || '').replace(/"/g, '');
    }
  } catch (_) {}
  return '';
};

// O tema (Metronic) usa content.width=fixed → o conteúdo vive num
// `.container` com max-width. Aqui, e só na rota do Drive, neutralizamos esse
// container (via :has, mirando o ancestral) para o iframe ocupar toda a
// largura disponível da área de conteúdo — sem tocar no layout global.
const FullBleed = createGlobalStyle`
  .container:has(> .drive-fullbleed),
  .container:has(.drive-fullbleed) {
    max-width: none;
    padding-left: 0;
    padding-right: 0;
  }
`;

const IframeWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  display: block;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

interface DriveFrameProps {
  path?: string;
}

const DriveFrame: React.FC<DriveFrameProps> = ({ path = '/' }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Link para um documento (`/meu-drive?openPreview=<uuid>`): o Drive abre na
  // pasta do arquivo com a visualização aberta. O param só entra na URL do iframe
  // quando é UUID e só na rota raiz do Drive.
  //
  // O id fica em estado: depois de entregue ao iframe, o param sai da URL do HOST
  // (abaixo) para um F5 não reabrir a visualização, e a `src` do iframe não pode
  // mudar por causa disso (recarregaria o Drive). O iframe tira o param da URL
  // dele por conta própria; a barra de endereço do monólito é problema do host.
  const history = useHistory();
  const { pathname, search, hash } = useLocation();
  const [openPreviewId, setOpenPreviewId] = useState<string | undefined>(() =>
    path === '/' ? readOpenPreviewParam(search) : undefined,
  );

  useEffect(() => {
    if (path !== '/') return;

    // Outro link com o Drive já aberto: leva o novo id ao iframe.
    const incomingId = readOpenPreviewParam(search);
    if (incomingId) setOpenPreviewId(incomingId);

    // Param entregue (ou inválido): sai da barra de endereço do host.
    if (new URLSearchParams(search).has(OPEN_PREVIEW_PARAM)) {
      history.replace({
        pathname,
        search: stripOpenPreviewParam(search),
        hash,
      });
    }
  }, [path, pathname, search, hash, history]);

  const src = `${config.drive.url}${path}?embedded=true${
    openPreviewId ? `&${OPEN_PREVIEW_PARAM}=${openPreviewId}` : ''
  }`;

  const { handleDriveOpenPlanExploration, planExplorationModal } = useDrivePlanExplorationBridge();
  /*
    Ref pelo mesmo motivo do resto do bridge deste arquivo: o efeito abaixo
    registra os listeners UMA VEZ (deps `[]`), e `handleDriveOpenPlanExploration`
    ganha identidade nova a cada render (usePlanExploration não memoiza
    `openPlanExploration`) — colocar a função direto nas deps reiniciaria o
    listener a cada render em vez de só ler o handler atual.
  */
  const handleDriveOpenPlanExplorationRef = useRef(handleDriveOpenPlanExploration);
  useEffect(() => {
    handleDriveOpenPlanExplorationRef.current = handleDriveOpenPlanExploration;
  }, [handleDriveOpenPlanExploration]);

  useEffect(() => {
    if (!config.drive.url) return undefined;
    const iframeOrigin = new URL(config.drive.url).origin;

    function handleMessage(event: MessageEvent) {
      if (event.origin !== iframeOrigin) return;
      if (event.data?.type !== 'DRIVE_IFRAME_READY') return;

      const token = getToken();
      if (!token) return;

      iframeRef.current?.contentWindow?.postMessage(
        { type: 'JUSFY_AUTH_TOKEN', token },
        iframeOrigin,
      );
    }

    // Bridge de analytics (mesmo padrão do CustomerFrame): o microfrontend não
    // carrega o Segment; envia SEGMENT_TRACK e o host repassa ao analytics.track
    // injetando owner/grupo. A identidade (user_id/client_id) já está no
    // analytics.identify feito no login.
    function handleAnalyticsMessage(event: MessageEvent) {
      if (event.origin !== iframeOrigin) return;
      if (event.data?.type !== 'SEGMENT_TRACK') return;

      const eventName = event.data?.payload?.event ?? '';
      const properties = event.data?.payload?.properties ?? {};
      const component = event.data?.payload?.component ?? 'iframe';

      if (typeof eventName !== 'string' || !eventName.trim()) return;
      if (!ALLOWED_DRIVE_IFRAME_SEGMENT_EVENTS.has(eventName)) return;
      if (!window.analytics?.track) return;

      const eventProperties: AnalyticsEvent = {
        event: eventName,
        properties: {
          ...properties,
          owner: OwnerEnum.AGC,
          relevant_user_activity: true,
          feature: FeatureEnum.DRIVE,
          ...(HUBSPOT_FILTER_DRIVE_IFRAME_EVENTS.has(eventName)
            ? { filtro_hubspot: 1 }
            : {}),
          // Vem do iframe: filho que não manda o campo derrubaria o handler no
          // Zod do tracker. O nome do evento é o significado mínimo aceitável.
          // ponytail: só anti-crash — significado pobre até o iframe filho mandar
          // o campo. Significado de verdade vem na DT de taxonomia.
          event_meaning:
            typeof properties.event_meaning === 'string' &&
            properties.event_meaning.trim()
              ? properties.event_meaning
              : eventName,
        },
        context: {
          groupId: GroupIdEnum.AGC_GROUP,
          library: {
            name: 'agc-library',
            componente: component,
          },
        },
      };

      eventTracker.track(eventProperties);
    }

    // Pedido do filho para abrir o modal de troca de plano — clique em "Ver
    // limite" ou upload barrado por cota. `reason` cruza de outro documento:
    // só repassa os dois valores que o contrato define.
    function handlePlanExplorationMessage(event: MessageEvent) {
      if (event.origin !== iframeOrigin) return;
      if (event.data?.type !== 'DRIVE_OPEN_PLAN_EXPLORATION') return;

      const reason = event.data?.reason;
      if (reason !== 'view_limit' && reason !== 'upload_blocked') return;

      handleDriveOpenPlanExplorationRef.current(reason as DrivePlanExplorationReason);
    }

    window.addEventListener('message', handleMessage);
    window.addEventListener('message', handleAnalyticsMessage);
    window.addEventListener('message', handlePlanExplorationMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('message', handleAnalyticsMessage);
      window.removeEventListener('message', handlePlanExplorationMessage);
    };
  }, []);

  const handleLoad = useCallback(async () => {
    setIsLoading(false);
  }, []);

  return (
    <>
      <IframeWrapper className="drive-fullbleed">
        <FullBleed />
        {isLoading && (
          <LoadingOverlay>
            <div className="spinner spinner-primary spinner-lg" />
          </LoadingOverlay>
        )}
        <StyledIframe
          ref={iframeRef}
          src={src}
          title="Drive"
          allow="clipboard-write"
          onLoad={handleLoad}
        />
      </IframeWrapper>
      {planExplorationModal}
    </>
  );
};

export default DriveFrame;
